import React, { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Tab,
  Tabs,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { Close as CloseIcon, OpenInNew as OpenIcon, Search as SearchIcon } from '@mui/icons-material';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  LabelList,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type {
  ShareLinkAgentProjectStat,
  ShareLinkAnalyticsLink,
  ShareLinkAnalyticsResponse,
} from '../../../services/api';
import {
  formatInt,
  formatRelative,
  formatTimestamp,
  METRIC_LABEL,
  ShareMetric,
  ShareVizPalette,
} from './shareLinksAnalyticsModel';
import {
  LegendKey,
  overlineSx,
  segmentedToggleSx,
  shareCardSx,
  ShareSortableTable,
  ShareTableColumn,
  ShareTooltipRow,
  ShareTooltipShell,
} from './shareLinksUi';

export type RankingKind = 'project' | 'agent';

interface RankItem {
  id: string;
  name: string;
  /** Second line under the name (CEA / mobile for agents, project id for projects). */
  sub: string;
  links: number;
  leads: number;
  opens: number;
  /** Agents sharing (project) or projects shared (agent). */
  breadth: number;
  lastSharedAt: string | null;
  lastLeadAt: string | null;
}

const BREADTH_LABEL: Record<RankingKind, string> = { project: 'Agents sharing', agent: 'Projects shared' };
const NOUN: Record<RankingKind, { one: string; many: string }> = {
  project: { one: 'project', many: 'projects' },
  agent: { one: 'agent', many: 'agents' },
};

const formatMobile = (raw: string): string => {
  const digits = raw.replace(/\D/g, '').slice(-8);
  return digits.length === 8 ? `${digits.slice(0, 4)} ${digits.slice(4)}` : raw;
};

const truncate = (text: string, max: number): string => (text.length > max ? `${text.slice(0, max - 1)}…` : text);

function toItems(kind: RankingKind, data: ShareLinkAnalyticsResponse): RankItem[] {
  if (kind === 'project') {
    return data.projects.map((p) => ({
      id: p.projectId,
      name: p.projectName,
      sub: p.projectId,
      links: p.links,
      leads: p.leads,
      opens: p.opens,
      breadth: p.sharers,
      lastSharedAt: p.lastSharedAt,
      lastLeadAt: p.lastLeadAt,
    }));
  }
  return data.agents.map((a) => ({
    id: a.ceaNumber,
    name: a.name,
    sub: a.mobile ? `${a.ceaNumber} · ${formatMobile(a.mobile)}` : a.ceaNumber,
    links: a.links,
    leads: a.leads,
    opens: a.opens,
    breadth: a.projects,
    lastSharedAt: a.lastSharedAt,
    lastLeadAt: a.lastLeadAt,
  }));
}

const dialogPaperSx = {
  bgcolor: 'var(--color-surface)',
  color: 'var(--color-text-strong)',
  backgroundImage: 'none',
} as const;

const dialogTabsSx = {
  minHeight: 40,
  mb: 1.5,
  borderBottom: '1px solid rgba(var(--color-text-rgb), 0.08)',
  '& .MuiTab-root': { minHeight: 40, textTransform: 'none', fontWeight: 600, color: 'rgba(var(--color-text-rgb), 0.65)' },
  '& .Mui-selected': { color: 'var(--color-accent) !important' },
  '& .MuiTabs-indicator': { backgroundColor: 'var(--color-accent)' },
} as const;

const timeCell = (value: string | null) => (
  <Tooltip title={formatTimestamp(value)} disableHoverListener={!value}>
    <Box component="span" sx={{ whiteSpace: 'nowrap', color: 'rgba(var(--color-text-rgb), 0.72)' }}>{formatRelative(value)}</Box>
  </Tooltip>
);

const numberCell = (value: number, strong?: boolean) => (
  <Box component="span" sx={{ fontWeight: strong ? 700 : 500 }}>{formatInt(value)}</Box>
);

const StatBlock: React.FC<{ label: string; value: string; color?: string }> = ({ label, value, color }) => (
  <Box
    sx={{
      flex: '1 1 120px',
      p: 1.5,
      borderRadius: 1.5,
      bgcolor: 'rgba(var(--color-text-rgb), 0.04)',
      borderLeft: color ? `3px solid ${color}` : '3px solid transparent',
    }}
  >
    <Typography sx={{ ...overlineSx, fontSize: '0.62rem' }}>{label}</Typography>
    <Typography sx={{ fontSize: '1.4rem', fontWeight: 700, color: 'var(--color-text-strong)', lineHeight: 1.2 }}>{value}</Typography>
  </Box>
);

// --- Drill-in dialog ----------------------------------------------------------

interface DrillDialogProps {
  kind: RankingKind;
  item: RankItem | null;
  rank: number;
  data: ShareLinkAnalyticsResponse;
  palette: ShareVizPalette;
  onClose: () => void;
}

const ShareLinksDrillDialog: React.FC<DrillDialogProps> = ({ kind, item, rank, data, palette, onClose }) => {
  const [tab, setTab] = useState<'breakdown' | 'links'>('breakdown');

  const agentName = useMemo(() => new Map(data.agents.map((a) => [a.ceaNumber, a.name])), [data.agents]);
  const projectName = useMemo(() => new Map(data.projects.map((p) => [p.projectId, p.projectName])), [data.projects]);

  const breakdownRows = useMemo<ShareLinkAgentProjectStat[]>(() => (
    item ? data.pairs.filter((p) => (kind === 'project' ? p.projectId === item.id : p.ceaNumber === item.id)) : []
  ), [data.pairs, kind, item]);

  const linkRows = useMemo<ShareLinkAnalyticsLink[]>(() => (
    item ? data.links.filter((l) => (kind === 'project' ? l.projectId === item.id : l.ceaNumber === item.id)) : []
  ), [data.links, kind, item]);

  const breakdownColumns: ShareTableColumn<ShareLinkAgentProjectStat>[] = [
    kind === 'project'
      ? {
        key: 'name',
        label: 'Agent',
        sortValue: (r) => agentName.get(r.ceaNumber) ?? r.ceaNumber,
        render: (r) => (
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>{agentName.get(r.ceaNumber) ?? r.ceaNumber}</Typography>
            <Typography variant="caption" sx={{ color: 'rgba(var(--color-text-rgb), 0.55)', fontFamily: 'monospace' }}>{r.ceaNumber}</Typography>
          </Box>
        ),
      }
      : {
        key: 'name',
        label: 'Project',
        sortValue: (r) => projectName.get(r.projectId) ?? r.projectId,
        render: (r) => <Typography variant="body2" sx={{ fontWeight: 600 }}>{projectName.get(r.projectId) ?? r.projectId}</Typography>,
      },
    { key: 'links', label: 'Links shared', align: 'right', sortValue: (r) => r.links, render: (r) => numberCell(r.links, true), meter: { value: (r) => r.links, color: palette.links }, width: 110 },
    { key: 'leads', label: 'Leads received', align: 'right', sortValue: (r) => r.leads, render: (r) => numberCell(r.leads, true), meter: { value: (r) => r.leads, color: palette.leads }, width: 120 },
    { key: 'opens', label: 'Opens', align: 'right', sortValue: (r) => r.opens, render: (r) => numberCell(r.opens), width: 80 },
    { key: 'lastShared', label: 'Last shared', align: 'right', sortValue: (r) => r.lastSharedAt ?? '', render: (r) => timeCell(r.lastSharedAt), width: 110 },
    { key: 'lastLead', label: 'Last lead', align: 'right', sortValue: (r) => r.lastLeadAt ?? '', render: (r) => timeCell(r.lastLeadAt), width: 100 },
  ];

  const linkColumns: ShareTableColumn<ShareLinkAnalyticsLink>[] = [
    { key: 'created', label: 'Created', sortValue: (l) => l.createdAt ?? '', render: (l) => timeCell(l.createdAt), width: 100 },
    {
      key: 'who',
      label: kind === 'project' ? 'Agent' : 'Project',
      sortValue: (l) => (kind === 'project' ? agentName.get(l.ceaNumber) ?? l.ceaNumber : projectName.get(l.projectId) ?? l.projectId),
      render: (l) => (
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {kind === 'project' ? agentName.get(l.ceaNumber) ?? l.ceaNumber : projectName.get(l.projectId) ?? l.projectId}
          </Typography>
          <Typography variant="caption" sx={{ color: 'rgba(var(--color-text-rgb), 0.55)', display: 'block', maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {[l.description, l.shortUrl].filter(Boolean).join(' · ') || '—'}
          </Typography>
        </Box>
      ),
    },
    { key: 'opens', label: 'Opens', align: 'right', sortValue: (l) => l.opens, render: (l) => numberCell(l.opens), meter: { value: (l) => l.opens, color: palette.opens }, width: 90 },
    {
      key: 'leads',
      label: <Tooltip title="Leads received in this period / all-time on this link"><span>Leads</span></Tooltip>,
      align: 'right',
      sortValue: (l) => l.leadsInRange,
      render: (l) => (
        <Box component="span">
          <Box component="span" sx={{ fontWeight: 700 }}>{formatInt(l.leadsInRange)}</Box>
          {l.leadsAllTime !== l.leadsInRange ? (
            <Box component="span" sx={{ color: 'rgba(var(--color-text-rgb), 0.5)' }}>{` / ${formatInt(l.leadsAllTime)}`}</Box>
          ) : null}
        </Box>
      ),
      width: 90,
    },
    {
      key: 'status',
      label: 'Status',
      align: 'right',
      sortValue: (l) => (l.revokedAt ? 2 : l.sharedInRange ? 0 : 1),
      render: (l) => (l.revokedAt
        ? <Chip size="small" label="Revoked" variant="outlined" color="error" sx={{ height: 20, fontSize: '0.68rem' }} />
        : l.sharedInRange
          ? <Chip size="small" label="Live" variant="outlined" color="success" sx={{ height: 20, fontSize: '0.68rem' }} />
          : (
            <Tooltip title="Shared before this period; listed for the leads it received in it">
              <Chip size="small" label="Earlier" variant="outlined" sx={{ height: 20, fontSize: '0.68rem' }} />
            </Tooltip>
          )),
      width: 96,
    },
  ];

  return (
    <Dialog open={!!item} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: dialogPaperSx }}>
      {item ? (
        <>
          <DialogTitle sx={{ borderBottom: '1px solid rgba(var(--color-text-rgb), 0.08)', pr: 7 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box
                sx={{
                  minWidth: 40,
                  height: 40,
                  px: 1,
                  borderRadius: 1.5,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: 'rgba(var(--color-accent-rgb), 0.14)',
                  color: 'var(--color-accent)',
                  fontWeight: 800,
                }}
              >
                {`#${rank}`}
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>{item.name}</Typography>
                <Typography variant="caption" sx={{ color: 'rgba(var(--color-text-rgb), 0.6)', fontFamily: 'monospace' }}>
                  {kind === 'agent' ? item.sub : `Project ID ${item.sub}`}
                </Typography>
              </Box>
            </Box>
            <IconButton onClick={onClose} size="small" sx={{ position: 'absolute', right: 12, top: 14 }} aria-label="Close">
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ pt: '20px !important' }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 2.5 }}>
              <StatBlock label="Links shared" value={formatInt(item.links)} color={palette.links} />
              <StatBlock label="Leads received" value={formatInt(item.leads)} color={palette.leads} />
              <StatBlock label={BREADTH_LABEL[kind]} value={formatInt(item.breadth)} />
              <StatBlock label="Opens (all-time)" value={formatInt(item.opens)} color={palette.opens} />
              <StatBlock label="Last shared" value={formatRelative(item.lastSharedAt)} />
              <StatBlock label="Last lead" value={formatRelative(item.lastLeadAt)} />
            </Box>
            <Tabs value={tab} onChange={(_, value) => setTab(value)} sx={dialogTabsSx}>
              <Tab value="breakdown" label={kind === 'project' ? `Agents who shared it (${breakdownRows.length})` : `Projects shared (${breakdownRows.length})`} />
              <Tab value="links" label={`Links (${linkRows.length})`} />
            </Tabs>
            {tab === 'breakdown' ? (
              <ShareSortableTable
                key={`breakdown-${item.id}`}
                rows={breakdownRows}
                columns={breakdownColumns}
                rowKey={(r) => `${r.ceaNumber}|${r.projectId}`}
                defaultSort={{ key: 'leads', dir: 'desc' }}
                maxHeight={440}
                emptyText="Nothing to break down."
              />
            ) : (
              <ShareSortableTable
                key={`links-${item.id}`}
                rows={linkRows}
                columns={linkColumns}
                rowKey={(l) => String(l.id)}
                defaultSort={{ key: 'created', dir: 'desc' }}
                maxHeight={440}
                emptyText="No links."
              />
            )}
          </DialogContent>
        </>
      ) : null}
    </Dialog>
  );
};

// --- Full list dialog ---------------------------------------------------------

interface AllDialogProps {
  kind: RankingKind;
  open: boolean;
  ranked: RankItem[];
  rankBy: ShareMetric;
  palette: ShareVizPalette;
  onClose: () => void;
  onSelect: (item: RankItem) => void;
}

const ShareLinksAllDialog: React.FC<AllDialogProps> = ({ kind, open, ranked, rankBy, palette, onClose, onSelect }) => {
  const [search, setSearch] = useState('');
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return q ? ranked.filter((i) => i.name.toLowerCase().includes(q) || i.sub.toLowerCase().includes(q)) : ranked;
  }, [ranked, search]);

  const columns: ShareTableColumn<RankItem>[] = [
    { key: 'rank', label: '#', render: (i) => <Box component="span" sx={{ color: 'rgba(var(--color-text-rgb), 0.55)' }}>{ranked.indexOf(i) + 1}</Box>, width: 44 },
    {
      key: 'name',
      label: kind === 'project' ? 'Project' : 'Agent',
      sortValue: (i) => i.name,
      render: (i) => (
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>{i.name}</Typography>
          {kind === 'agent' ? <Typography variant="caption" sx={{ color: 'rgba(var(--color-text-rgb), 0.55)', fontFamily: 'monospace' }}>{i.sub}</Typography> : null}
        </Box>
      ),
    },
    { key: 'links', label: 'Links shared', align: 'right', sortValue: (i) => i.links, render: (i) => numberCell(i.links, true), meter: { value: (i) => i.links, color: palette.links }, width: 110 },
    { key: 'leads', label: 'Leads received', align: 'right', sortValue: (i) => i.leads, render: (i) => numberCell(i.leads, true), meter: { value: (i) => i.leads, color: palette.leads }, width: 120 },
    { key: 'breadth', label: BREADTH_LABEL[kind], align: 'right', sortValue: (i) => i.breadth, render: (i) => numberCell(i.breadth), width: 120 },
    { key: 'opens', label: 'Opens', align: 'right', sortValue: (i) => i.opens, render: (i) => numberCell(i.opens), width: 80 },
    { key: 'lastShared', label: 'Last shared', align: 'right', sortValue: (i) => i.lastSharedAt ?? '', render: (i) => timeCell(i.lastSharedAt), width: 110 },
    { key: 'lastLead', label: 'Last lead', align: 'right', sortValue: (i) => i.lastLeadAt ?? '', render: (i) => timeCell(i.lastLeadAt), width: 100 },
    { key: 'open', label: '', align: 'right', render: () => <OpenIcon sx={{ fontSize: 16, color: 'rgba(var(--color-text-rgb), 0.45)' }} />, width: 36 },
  ];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth PaperProps={{ sx: dialogPaperSx }}>
      <DialogTitle sx={{ borderBottom: '1px solid rgba(var(--color-text-rgb), 0.08)', pr: 7 }}>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>{kind === 'project' ? 'All projects' : 'All agents'}</Typography>
        <Typography variant="body2" sx={{ color: 'rgba(var(--color-text-rgb), 0.62)' }}>
          {`${formatInt(ranked.length)} ${ranked.length === 1 ? NOUN[kind].one : NOUN[kind].many} in this period. Click a row to drill in.`}
        </Typography>
        <IconButton onClick={onClose} size="small" sx={{ position: 'absolute', right: 12, top: 14 }} aria-label="Close">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ pt: '20px !important' }}>
        <TextField
          size="small"
          placeholder={kind === 'project' ? 'Search projects' : 'Search by name, CEA or mobile'}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ mb: 1.5, width: 340, maxWidth: '100%' }}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
        />
        <ShareSortableTable
          rows={filtered}
          columns={columns}
          rowKey={(i) => i.id}
          defaultSort={{ key: rankBy, dir: 'desc' }}
          maxHeight={520}
          emptyText="No matches."
          onRowClick={onSelect}
        />
      </DialogContent>
    </Dialog>
  );
};

// --- Ranking card -------------------------------------------------------------

interface ShareLinksRankingSectionProps {
  kind: RankingKind;
  title: string;
  subtitle: string;
  topN: number;
  data: ShareLinkAnalyticsResponse;
  palette: ShareVizPalette;
  tickColor: string;
  inkColor: string;
}

const ShareLinksRankingSection: React.FC<ShareLinksRankingSectionProps> = ({
  kind,
  title,
  subtitle,
  topN,
  data,
  palette,
  tickColor,
  inkColor,
}) => {
  const [rankBy, setRankBy] = useState<ShareMetric>('leads');
  const [drillId, setDrillId] = useState<string | null>(null);
  const [allOpen, setAllOpen] = useState(false);

  const items = useMemo(() => toItems(kind, data), [kind, data]);
  const ranked = useMemo(() => {
    const other: ShareMetric = rankBy === 'links' ? 'leads' : 'links';
    return [...items].sort((a, b) => b[rankBy] - a[rankBy] || b[other] - a[other] || a.name.localeCompare(b.name));
  }, [items, rankBy]);
  const top = useMemo(() => ranked.filter((i) => i[rankBy] > 0).slice(0, topN), [ranked, rankBy, topN]);

  const drillItem = items.find((i) => i.id === drillId) ?? null;
  const drillRank = drillItem ? ranked.findIndex((i) => i.id === drillItem.id) + 1 : 0;

  const rowHeight = 52;
  const chartHeight = Math.max(top.length, 3) * rowHeight + 40;
  const labelWidth = 150;
  const tick = { fill: tickColor, fontSize: 11 };

  const CategoryTick = (props: { x?: number | string; y?: number | string; payload?: { value?: string; index?: number } }) => {
    const label = String(props.payload?.value ?? '');
    const item = top.find((i) => i.name === label);
    return (
      <g
        transform={`translate(${Number(props.x)},${Number(props.y)})`}
        style={{ cursor: 'pointer' }}
        onClick={(e) => {
          // Don't let the chart's own click (hover index) pick a different row.
          e.stopPropagation();
          if (item) setDrillId(item.id);
        }}
      >
        <text x={-8} y={0} dy={4} textAnchor="end" fontSize={12} fontWeight={600} fill={inkColor}>
          {truncate(label, 18)}
        </text>
      </g>
    );
  };

  const RankingTooltip: React.FC<{ active?: boolean; payload?: Array<{ payload?: RankItem }> }> = ({ active, payload }) => {
    const item = payload?.[0]?.payload;
    if (!active || !item) return null;
    return (
      <ShareTooltipShell title={item.name}>
        <ShareTooltipRow color={palette.links} label={METRIC_LABEL.links} value={formatInt(item.links)} strong />
        <ShareTooltipRow color={palette.leads} label={METRIC_LABEL.leads} value={formatInt(item.leads)} strong />
        <ShareTooltipRow label={BREADTH_LABEL[kind]} value={formatInt(item.breadth)} />
        <ShareTooltipRow label="Opens (all-time)" value={formatInt(item.opens)} />
        <Typography variant="caption" sx={{ color: 'rgba(var(--color-text-rgb), 0.5)', pt: 0.5 }}>Click to drill in</Typography>
      </ShareTooltipShell>
    );
  };

  return (
    <Box sx={{ ...shareCardSx, display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', mb: 1.5 }}>
        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.3 }}>{title}</Typography>
          <Typography variant="body2" sx={{ color: 'rgba(var(--color-text-rgb), 0.62)' }}>{subtitle}</Typography>
        </Box>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={rankBy}
          onChange={(_, value: ShareMetric | null) => value && setRankBy(value)}
          sx={segmentedToggleSx}
          aria-label="Rank by"
        >
          <ToggleButton value="leads">By leads</ToggleButton>
          <ToggleButton value="links">By links</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      <Box sx={{ display: 'flex', gap: 2, mb: 1 }}>
        <LegendKey color={palette.links} label={METRIC_LABEL.links} />
        <LegendKey color={palette.leads} label={METRIC_LABEL.leads} />
      </Box>

      {top.length === 0 ? (
        <Typography variant="body2" sx={{ py: 6, textAlign: 'center', color: 'rgba(var(--color-text-rgb), 0.55)' }}>
          {`No ${NOUN[kind].many} with ${METRIC_LABEL[rankBy].toLowerCase()} in this period.`}
        </Typography>
      ) : (
        <Box sx={{ width: '100%', flex: 1, minHeight: chartHeight }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={top}
              layout="vertical"
              margin={{ top: 4, right: 40, left: 0, bottom: 4 }}
              barGap={2}
              barCategoryGap="24%"
              onClick={(state) => {
                const index = Number(state?.activeTooltipIndex);
                if (Number.isInteger(index) && top[index]) setDrillId(top[index].id);
              }}
              style={{ cursor: 'pointer' }}
            >
              <CartesianGrid stroke={palette.grid} horizontal={false} />
              <XAxis type="number" allowDecimals={false} tick={tick} stroke={palette.axis} tickLine={false} />
              <YAxis type="category" dataKey="name" width={labelWidth} tick={CategoryTick} stroke={palette.axis} tickLine={false} interval={0} />
              <RechartsTooltip content={<RankingTooltip />} cursor={{ fill: palette.selection }} />
              <Bar dataKey="links" name={METRIC_LABEL.links} fill={palette.links} radius={[0, 4, 4, 0]} maxBarSize={14} isAnimationActive={false}>
                {top.map((item) => <Cell key={item.id} />)}
                <LabelList dataKey="links" position="right" fill={tickColor} fontSize={11} fontWeight={600} />
              </Bar>
              <Bar dataKey="leads" name={METRIC_LABEL.leads} fill={palette.leads} radius={[0, 4, 4, 0]} maxBarSize={14} isAnimationActive={false}>
                {top.map((item) => <Cell key={item.id} />)}
                <LabelList dataKey="leads" position="right" fill={tickColor} fontSize={11} fontWeight={600} />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Box>
      )}

      <Box sx={{ mt: 'auto', pt: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderTop: '1px solid rgba(var(--color-text-rgb), 0.08)' }}>
        <Typography variant="caption" sx={{ color: 'rgba(var(--color-text-rgb), 0.55)' }}>Click a bar to drill in</Typography>
        <Button
          size="small"
          endIcon={<OpenIcon sx={{ fontSize: '16px !important' }} />}
          onClick={() => setAllOpen(true)}
          sx={{ textTransform: 'none', fontWeight: 600, color: 'var(--color-accent)' }}
        >
          {`View all ${formatInt(items.length)} ${items.length === 1 ? NOUN[kind].one : NOUN[kind].many}`}
        </Button>
      </Box>

      <ShareLinksAllDialog
        kind={kind}
        open={allOpen}
        ranked={ranked}
        rankBy={rankBy}
        palette={palette}
        onClose={() => setAllOpen(false)}
        onSelect={(item) => setDrillId(item.id)}
      />
      {/* Mounted last so it stacks above the list dialog when opened from a row. */}
      <ShareLinksDrillDialog kind={kind} item={drillItem} rank={drillRank} data={data} palette={palette} onClose={() => setDrillId(null)} />
    </Box>
  );
};

export default ShareLinksRankingSection;
